/**
 * GlossaryTooltip — hover/focus definition for a piece of jargon, read from the
 * shared glossary (CLARID-UX-2026-06, PR 3). Fixes audit finding F3 (pervasive
 * unexplained acronyms) by defining each term once and surfacing it inline.
 *
 * Pure CSS reveal (group-hover / group-focus-within) — no JS state, no portal.
 * Keyboard-accessible via a focusable trigger. Pass `children` to label the
 * trigger with the in-context text (e.g. wrap "MMCT" in a table header); omit it
 * to render the glossary's canonical term.
 */

import { glossary } from '@/lib/help';
import type { GlossaryTermKey } from '@/lib/help';

interface GlossaryTooltipProps {
  termKey: GlossaryTermKey;
  /** In-context label; defaults to the glossary's canonical term. */
  children?: React.ReactNode;
}

export default function GlossaryTooltip({ termKey, children }: GlossaryTooltipProps) {
  const entry = glossary[termKey];
  if (!entry) return <>{children}</>;

  return (
    <span className="group relative inline-flex">
      <span
        tabIndex={0}
        className="cursor-help border-b border-dotted border-gray-400 text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400"
      >
        {children ?? entry.term}
      </span>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-0 top-full z-50 mt-1.5 w-64 max-w-[16rem] rounded-lg bg-gray-900 px-3 py-2 text-left text-xs font-normal leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
      >
        <span className="font-semibold text-white">{entry.term}</span>
        <span className="mt-0.5 block text-gray-200">{entry.plainDefinition}</span>
        {entry.regCite && <span className="mt-1 block text-gray-400">{entry.regCite}</span>}
      </span>
    </span>
  );
}
